import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { ChartTooltipProvider, StateBlock, ToastProvider } from '@w7/ui'
import { AppShell } from './AppShell'
import { AuthGate } from './AuthGate'
import { StaffGrantGate } from './StaffGrantGate'
import { DateRangeProvider } from './range'
import { ThemeProvider } from './theme'
import { GlobalSearchProvider } from '@/features/search'
import { ToastFlash } from '@/ui/ToastFlash'

import { LoginPage } from '@/features/auth/LoginPage'
import { CallbackPage } from '@/features/auth/CallbackPage'
import { NoAccessPage } from '@/features/auth/NoAccessPage'
import { OverviewPage } from '@/features/overview/OverviewPage'
import { DownlinePage } from '@/features/downline/DownlinePage'
import { MemberPage } from '@/features/member/MemberPage'
import { CreateAccountPage } from '@/features/create/CreateAccountPage'
import { ExposurePage } from '@/features/exposure/ExposurePage'
import { MarketBookPage } from '@/features/exposure/MarketBookPage'
import { BookManagementWorkspace } from '@/features/book-management/WorkspacePage'
import { BookManagementAccessGate } from '@/features/book-management/BookManagementAccessGate'
import { NetExposurePage } from '@/features/net-exposure/NetExposurePage'
import { WorkstationPage } from '@/features/net-exposure/WorkstationPage'
import { AdvanceWorkstationPage } from '@/features/net-exposure/AdvanceWorkstationPage'
import { MyPnlPage } from '@/features/reports/MyPnlPage'
import { PlStatementPage } from '@/features/reports/PlStatementPage'
import { TransferStatementPage } from '@/features/reports/TransferStatementPage'
import { TurnoverPage } from '@/features/reports/TurnoverPage'
import { CommissionPage } from '@/features/reports/CommissionPage'
import { CommissionEligibilityPage } from '@/features/reports/CommissionEligibilityPage'
import { FancyBookmakerCommissionPage } from '@/features/reports/FancyBookmakerCommissionPage'
import { ActivityPage } from '@/features/activity/ActivityPage'
import { SecuritySettingsPage } from '@/features/settings/SecuritySettingsPage'
import { MfaEnrollPage } from '@/features/settings/MfaEnrollPage'
import { Player2faPage } from '@/features/player-2fa/Player2faPage'
import { VersionSettingsPage } from '@/features/version/VersionSettingsPage'
import { HierarchySearchPage } from '@/features/hierarchy-search/HierarchySearchPage'
import { UsersPage } from '@/features/users/UsersPage'
import { UserFormPage } from '@/features/users/UserFormPage'
import { LogAccessGate } from '@/features/logs/LogAccessGate'
import { ClTransfersPage } from '@/features/logs/ClTransfersPage'
import { MultipleDeviceIdsPage } from '@/features/logs/MultipleDeviceIdsPage'
import { InvalidBetsPage } from '@/features/logs/InvalidBetsPage'
import { BlockedDeviceIdsPage } from '@/features/logs/BlockedDeviceIdsPage'
import { BetfairRatesLogsPage } from '@/features/logs/BetfairRatesLogsPage'
import { BlockedIpPage } from '@/features/logs/BlockedIpPage'
import { LoginFromBlockedIpPage } from '@/features/logs/LoginFromBlockedIpPage'

const client = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
})

export function App() {
  return (
    <QueryClientProvider client={client}>
      <ThemeProvider>
        <DateRangeProvider>
          <ToastProvider>
            <ChartTooltipProvider>
              <BrowserRouter>
                <ToastFlash />
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/callback" element={<CallbackPage />} />
                  <Route path="/no-access" element={<NoAccessPage />} />
                  <Route
                    path="*"
                    element={
                      <AuthGate>
                        {/* ⌘K. Three constraints fix this exact position:
                          · inside `<BrowserRouter>` — the palette navigates;
                          · inside `<AuthGate>` — every result is a downline
                            account, a market or a ledger ref, so the palette has
                            nothing it may show before there is a session, and
                            searching from /login would put an unauthenticated
                            read on the wire the moment the shortcut was pressed;
                          · OUTSIDE the inner `<Routes>` — it must survive the
                            navigation it causes. That works because this whole
                            `path="*"` element stays mounted across every in-app
                            route; only the inner `<Routes>` swaps. Mounted one
                            level deeper it would unmount itself mid-transition. */}
                        <GlobalSearchProvider>
                          <AppShell>
                            <StaffGrantGate>
                            <Routes>
                              <Route path="/" element={<Navigate to="/overview" replace />} />
                              <Route path="/overview" element={<OverviewPage />} />
                              <Route path="/downline" element={<DownlinePage />} />
                              <Route path="/members" element={<Navigate to="/downline" replace />} />
                              <Route path="/hierarchy-search" element={<HierarchySearchPage />} />
                              <Route path="/users" element={<UsersPage />} />
                              <Route path="/users/new" element={<UserFormPage />} />
                              <Route path="/users/:id" element={<UserFormPage />} />
                              <Route path="/member/:id" element={<MemberPage />} />
                              <Route path="/create" element={<CreateAccountPage />} />
                              <Route
                                path="/book-management"
                                element={<Navigate to="/book-management/net-exposure" replace />}
                              />
                              <Route path="/book-management/:mode" element={<BookManagementWorkspace />} />
                              <Route
                                path="/net-exposure"
                                element={
                                  <BookManagementAccessGate>
                                    <NetExposurePage />
                                  </BookManagementAccessGate>
                                }
                              />
                              <Route
                                path="/net-exposure/workstation/:marketId"
                                element={
                                  <BookManagementAccessGate>
                                    <WorkstationPage />
                                  </BookManagementAccessGate>
                                }
                              />
                              <Route
                                path="/net-exposure/advance-workstation/:id"
                                element={
                                  <BookManagementAccessGate>
                                    <AdvanceWorkstationPage />
                                  </BookManagementAccessGate>
                                }
                              />
                              <Route path="/exposure" element={<ExposurePage />} />
                              <Route path="/exposure/market/:marketId" element={<MarketBookPage />} />
                              <Route path="/reports/my-pnl" element={<MyPnlPage />} />
                              <Route path="/reports/pl-statement" element={<PlStatementPage />} />
                              <Route path="/PLStatement" element={<Navigate to="/reports/pl-statement" replace />} />
                              <Route path="/PLStatement/" element={<Navigate to="/reports/pl-statement" replace />} />
                              <Route path="/reports/transfers" element={<TransferStatementPage />} />
                              <Route path="/reports/turnover" element={<TurnoverPage />} />
                              <Route
                                path="/reports/commission/eligibility"
                                element={<CommissionEligibilityPage />}
                              />
                              <Route
                                path="/reports/commission/fancy-bookmaker"
                                element={<FancyBookmakerCommissionPage />}
                              />
                              <Route path="/reports/commission" element={<CommissionPage />} />
                              <Route path="/activity" element={<ActivityPage />} />
                              <Route
                                path="/logs/cl-transfers"
                                element={
                                  <LogAccessGate pageId="cl-transfers">
                                    <ClTransfersPage />
                                  </LogAccessGate>
                                }
                              />
                              <Route
                                path="/logs/multiple-device-ids"
                                element={
                                  <LogAccessGate pageId="multiple-device-ids">
                                    <MultipleDeviceIdsPage />
                                  </LogAccessGate>
                                }
                              />
                              <Route
                                path="/logs/betfair-rates"
                                element={
                                  <LogAccessGate pageId="betfair-rates">
                                    <BetfairRatesLogsPage />
                                  </LogAccessGate>
                                }
                              />
                              <Route
                                path="/logs/invalid-bets"
                                element={
                                  <LogAccessGate pageId="invalid-bets">
                                    <InvalidBetsPage />
                                  </LogAccessGate>
                                }
                              />
                              <Route
                                path="/logs/blocked-device-ids"
                                element={
                                  <LogAccessGate pageId="blocked-device-ids">
                                    <BlockedDeviceIdsPage />
                                  </LogAccessGate>
                                }
                              />
                              <Route
                                path="/logs/blocked-ip"
                                element={
                                  <LogAccessGate pageId="blocked-ip">
                                    <BlockedIpPage />
                                  </LogAccessGate>
                                }
                              />
                              <Route
                                path="/logs/login-from-blocked-ip"
                                element={
                                  <LogAccessGate pageId="login-from-blocked-ip">
                                    <LoginFromBlockedIpPage />
                                  </LogAccessGate>
                                }
                              />
                              <Route path="/player-2fa" element={<Player2faPage />} />
                              <Route path="/settings/security" element={<SecuritySettingsPage />} />
                              <Route path="/settings/security/:methodId" element={<MfaEnrollPage />} />
                              <Route path="/settings/version" element={<VersionSettingsPage />} />
                              <Route path="/reports" element={<Navigate to="/reports/my-pnl" replace />} />
                              <Route
                                path="*"
                                element={
                                  <StateBlock
                                    icon="search"
                                    title="That page does not exist"
                                  >
                                    The link may be stale, or it points at something outside your downline.
                                  </StateBlock>
                                }
                              />
                            </Routes>
                            </StaffGrantGate>
                          </AppShell>
                        </GlobalSearchProvider>
                      </AuthGate>
                    }
                  />
                </Routes>
              </BrowserRouter>
            </ChartTooltipProvider>
          </ToastProvider>
        </DateRangeProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
